import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  ListOrdersQuery,
  OrderCounts,
  OrderFilters,
  OrderListItemResource,
  OrderResource,
  UpdateOrderStatusInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['orders'] as const;

export interface OrderListPage {
  items: OrderListItemResource[];
  meta: CollectionMeta;
}

function filterQuery(q: OrderFilters): Record<string, string | number | undefined> {
  return {
    status: q.status,
    customer: q.customer,
    reference: q.reference,
    totalMin: q.totalMin,
    totalMax: q.totalMax,
    createdFrom: q.createdFrom,
    createdTo: q.createdTo,
  };
}

function toQuery(q: ListOrdersQuery): Record<string, string | number | undefined> {
  return {
    ...filterQuery(q),
    page: q.page,
    pageSize: q.pageSize,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listOrders(q: ListOrdersQuery = {}): Promise<OrderListPage> {
  const res = await apiClient.getCollection<OrderListItemResource>('/orders', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getOrder = (id: string) => apiClient.get<OrderResource>(`/orders/${id}`);
export const updateOrderStatus = (id: string, input: UpdateOrderStatusInput) =>
  apiClient.patch<OrderResource>(`/orders/${id}/status`, input);

export function useOrders(query: ListOrdersQuery = {}) {
  return useQuery<OrderListPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listOrders(query),
    placeholderData: keepPreviousData,
  });
}

/** All / per-status counts for the grid tabs, under the same filters (status ignored). */
export function useOrderCounts(query: OrderFilters = {}) {
  const { status: _status, ...filters } = query;
  return useQuery<OrderCounts, ApiClientError>({
    queryKey: [...KEY, 'counts', filters],
    queryFn: () => apiClient.get<OrderCounts>('/orders/counts', { query: filterQuery(filters) }),
    placeholderData: keepPreviousData,
  });
}

export function useOrder(id: string | undefined) {
  return useQuery<OrderResource, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getOrder(id!),
    enabled: Boolean(id),
  });
}

export function useUpdateOrderStatus(id: string) {
  const qc = useQueryClient();
  return useMutation<OrderResource, ApiClientError, UpdateOrderStatusInput>({
    mutationFn: (input) => updateOrderStatus(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
